/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {AcceptFriendRequestIcon, BlockUserIcon, RetryIcon} from '@app/components/uikit/context_menu/ContextMenuIcons';
import {MenuItem} from '@app/components/uikit/context_menu/MenuItem';
import type {UserRecord} from '@app/records/UserRecord';
import LocalUserSpamOverrideStore from '@app/stores/LocalUserSpamOverrideStore';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

interface SpammerOverrideMenuItemsProps {
	user: UserRecord;
	onClose: () => void;
	developerMode: boolean;
}

export function shouldShowSpammerOverrideMenuItems(params: {user: UserRecord; developerMode: boolean}): boolean {
	const isServerSpammer = LocalUserSpamOverrideStore.isServerSpammer(params.user.flags);
	const isLocalSpammer = LocalUserSpamOverrideStore.isLocallyMarkedSpammer(params.user.id);
	const isLocalNotSpammer = LocalUserSpamOverrideStore.isLocallyMarkedNotSpammer(params.user.id);
	const isEffectiveSpammer = LocalUserSpamOverrideStore.isUserMarkedAsSpammer(params.user.id, params.user.flags);
	const hasLocalOverride = isLocalSpammer || isLocalNotSpammer;
	const canMarkAsNotSpammer = isServerSpammer && !isLocalSpammer && isEffectiveSpammer && !isLocalNotSpammer;
	const canMarkAsSpammer = params.developerMode && !isEffectiveSpammer;
	const canClearOverride = hasLocalOverride;

	return canMarkAsNotSpammer || canMarkAsSpammer || canClearOverride;
}

export const SpammerOverrideMenuItems: React.FC<SpammerOverrideMenuItemsProps> = observer(
	({user, onClose, developerMode}) => {
		const {t} = useLingui();

		const isServerSpammer = LocalUserSpamOverrideStore.isServerSpammer(user.flags);
		const isLocalSpammer = LocalUserSpamOverrideStore.isLocallyMarkedSpammer(user.id);
		const isLocalNotSpammer = LocalUserSpamOverrideStore.isLocallyMarkedNotSpammer(user.id);
		const isEffectiveSpammer = LocalUserSpamOverrideStore.isUserMarkedAsSpammer(user.id, user.flags);
		const hasLocalOverride = isLocalSpammer || isLocalNotSpammer;

		const canMarkAsNotSpammer = isServerSpammer && !isLocalSpammer && isEffectiveSpammer && !isLocalNotSpammer;
		const canMarkAsSpammer = developerMode && !isEffectiveSpammer;
		const canClearOverride = hasLocalOverride;

		const handleMarkAsNotSpammer = useCallback(() => {
			LocalUserSpamOverrideStore.markAsNotSpammer(user.id);
			onClose();
		}, [user.id, onClose]);

		const handleMarkAsSpammer = useCallback(() => {
			LocalUserSpamOverrideStore.markAsSpammer(user.id);
			onClose();
		}, [user.id, onClose]);

		const handleClearOverride = useCallback(() => {
			LocalUserSpamOverrideStore.clearOverride(user.id);
			onClose();
		}, [user.id, onClose]);

		if (!shouldShowSpammerOverrideMenuItems({user, developerMode})) {
			return null;
		}

		return (
			<>
				{canMarkAsNotSpammer && (
					<MenuItem icon={<AcceptFriendRequestIcon />} onClick={handleMarkAsNotSpammer}>
						{t`Mark as Not Spammer`}
					</MenuItem>
				)}

				{canMarkAsSpammer && (
					<MenuItem icon={<BlockUserIcon />} onClick={handleMarkAsSpammer} danger>
						{t`Mark as Spammer`}
					</MenuItem>
				)}

				{canClearOverride && (
					<MenuItem icon={<RetryIcon />} onClick={handleClearOverride}>
						{t`Clear Local Spammer Override`}
					</MenuItem>
				)}
			</>
		);
	},
);
